import {IRole} from '@verdocs/js-sdk';
import React, {FC, useState, useEffect} from 'react';
import {ChevronDownIcon, ChevronUpIcon} from '@heroicons/react/24/outline';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter} from '../../../../Components';

interface RoleSettingsModalProps {
  role: IRole | null;
  open: boolean;
  onClose: () => void;
  onSave: (updates: Partial<IRole> & {invite_email?: boolean; invite_phone?: boolean; required?: boolean}) => void;
}

export const RoleSettingsModal: FC<RoleSettingsModalProps> = ({role, open, onClose, onSave}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [required, setRequired] = useState(true);
  const [delegator, setDelegator] = useState(false);
  const [nameLocked, setNameLocked] = useState(false);
  const [inviteEmail, setInviteEmail] = useState(true);
  const [invitePhone, setInvitePhone] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (role) {
      setFirstName(role.first_name || '');
      setLastName(role.last_name || '');
      setEmail(role.email || '');
      setPhone(role.phone || '');
      setDelegator(role.delegator || false);
      setNameLocked(role.name_locked || false);
      setRequired(true);
      setInviteEmail(true);
      setInvitePhone(!!role.phone);
    }
  }, [role]);

  useEffect(() => {
    if (!phone.trim()) {
      setInvitePhone(false);
    }
  }, [phone]);

  const handleSave = () => {
    onSave({
      first_name: firstName || null,
      last_name: lastName || null,
      email: email || null,
      phone: phone || null,
      delegator,
      name_locked: nameLocked,
      invite_email: inviteEmail,
      invite_phone: invitePhone,
      required,
    });
    onClose();
  };

  if (!role) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Role Settings: {role.name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="First name"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Last name"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="email@example.com"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 self-start mt-1">
            Advanced
            {showAdvanced ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
          </button>

          {showAdvanced && (
            <div className="flex flex-col gap-3 pl-2 border-l-2 border-gray-200">
              <label className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Required</span>
                <input
                  type="checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </label>

              <label className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Can delegate</span>
                <input
                  type="checkbox"
                  checked={delegator}
                  onChange={(e) => setDelegator(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </label>

              <label className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Lock name</span>
                <input
                  type="checkbox"
                  checked={nameLocked}
                  onChange={(e) => setNameLocked(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
              </label>

              <div className="border-t border-gray-200 pt-2 mt-1">
                <span className="text-sm font-medium text-gray-700">Invite via</span>
                <div className="flex flex-col gap-2 mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-gray-700">Email</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={invitePhone}
                      onChange={(e) => setInvitePhone(e.target.checked)}
                      disabled={!phone.trim()}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-40"
                    />
                    <span className={`text-sm ${!phone.trim() ? 'text-gray-400' : 'text-gray-700'}`}>
                      Phone {!phone.trim() && '(enter phone number first)'}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
            Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
